/**
 * AgentCreateSlideout — V2 right-rail Agentlet creation (Phase 5A).
 *
 * Replaces the in-place Modal that previously hosted "+ New Agentlet".
 * The modal had outgrown its shape after V188 / Phase 1A added the
 * five-dimension typing model (four mode pickers + advisory + execution
 * hint) on top of name / description / endpoint fields — the form is
 * now slideout-sized.
 *
 * Migration rules followed (per G378 Create-/Edit- slideout pattern):
 *   1. Lift the JSX from AgentsInventoryPageV2.jsx verbatim — NO
 *      behavioural changes. The form state (`createForm`), the submit
 *      handler (`submitCreate`), and the validation messages all stay
 *      with the host page; this component is presentation only.
 *   2. Footer becomes the slideout's sticky action bar (`footer` prop).
 *   3. Title transforms based on `archetype` exactly as the modal did.
 *   4. autoFocus on the name field is preserved.
 *
 * The component is intentionally a pure presentational shell — no API
 * calls, no toast emission, no SecurityContext reads. The host page
 * owns the create lifecycle; we just render the form.
 *
 * Caller contract:
 *   <AgentCreateSlideout
 *     isOpen={createOpen}
 *     onClose={closeCreate}
 *     archetype={createArchetype}     // 'let' | 'autolet' | 'extelet'
 *     form={createForm}
 *     setForm={setCreateForm}
 *     onSubmit={submitCreate}
 *     submitting={creating}
 *   />
 *
 * AutoLet and ExteLet paths still hand off to AutoCreateLauncher /
 * GelRegistrationLauncher respectively at the openCreate dispatcher
 * level — this slideout primarily serves the Let path. AutoLet/ExteLet
 * titles are kept in the switch so a host that explicitly mounts this
 * for those archetypes (e.g. a deep-link from a test harness) still
 * renders coherently.
 */
import React from 'react';
import { Button, Pill, Slideout } from './index';
import { archetypeShortLabel } from '../../data/agentArchetypes';
import {
  FUNCTION_MODE_OPTIONS, INDUSTRY_MODE_OPTIONS, OPERATION_MODE_OPTIONS,
  EXECUTION_MODE_OPTIONS,
} from '../../data/agentModes';
import AccountabilitySection from './AccountabilitySection';
import { Bot, CheckCircle2 } from 'lucide-react';


/* Step 1 (Platform-Eng workflow) — opinionated defaults the slideout
 * applies when control_plane is selected as function_mode for the
 * first time. We DO NOT overwrite an operator's manual choice — these
 * fire only when the corresponding field is still at its blank /
 * default value. */
const CONTROL_PLANE_DEFAULTS = {
  operationMode: 'human',            // every mutating action gated
  executionMode: 'deterministic',    // no LLM in critical path
};




export function AgentCreateSlideout({
  isOpen,
  onClose,
  archetype = 'let',
  form,
  setForm,
  onSubmit,
  submitting = false,
  onSaveDraft,
  savingDraft = false,
  draftId = null,
}) {
  if (!isOpen) return null;

  const title =
    archetype === 'extelet' ? 'Register external agent (ExteLet)' :
    archetype === 'autolet' ? 'Auto-create from policy (AutoLet)' :
    /* DEF-RBAC-SYSLET-001 — when Platform/Super Admin chose
       "Provision system agent" from the inventory dropdown, surface a
       title that matches so the operator sees the correct archetype
       framing. */
    archetype === 'syslet'  ? 'Provision system agent (SysLet)' :
    'Create native Agentlet (Let)';

  const submitLabel = submitting
    ? 'Creating…'
    : (archetype === 'extelet' ? 'Register'
       : archetype === 'syslet' ? 'Provision in Build phase'
       : 'Create in Build phase');

  // DEF-VALIDATION-001 fix — enforce a 3..100 character name window in the
  // UI. Previously the submit button was only disabled when the name was
  // empty, which allowed "AB" (2 chars) and 100+ character names to flow
  // through. The 100-char ceiling matters because the platform surfaces the
  // name verbatim in the delete-confirm modal, the audit-trail rows, and
  // the inventory table — a runaway name otherwise makes those surfaces
  // wrap awkwardly. Backend has the matching @Size constraint so a
  // misbehaving client (curl / E2E test) still gets a 400.
  const NAME_MIN = 3;
  const NAME_MAX = 100;
  const nameTrimmed = (form?.name || '').trim();
  const nameLen = nameTrimmed.length;
  const isNameValid = nameLen >= NAME_MIN && nameLen <= NAME_MAX;
  const nameHint = nameLen === 0
    ? `Required · ${NAME_MIN}–${NAME_MAX} characters`
    : nameLen < NAME_MIN
      ? `Too short · ${nameLen}/${NAME_MIN} characters minimum`
      : nameLen > NAME_MAX
        ? `Too long · ${nameLen}/${NAME_MAX} characters maximum`
        : `${nameLen} / ${NAME_MAX} characters`;
  const nameHintTone = nameLen === 0
    ? 'text-v2-ink-muted'
    : isNameValid
      ? 'text-v2-ink-muted'
      : 'text-v2-danger';

  const intro =
    archetype === 'extelet'
      ? 'External agents are governed via the GEL connector + a bound policy. Provide the endpoint and (optionally) an API key — the agent will appear in your inventory pending verification.'
      : archetype === 'autolet'
      ? 'AutoLets are minted from policy documents. For now, this creates a draft AutoLet you supervise during Probation; the policy-doc upload wizard lands in Phase 4 follow-up.'
      : 'Native Agentlets are authored hand-to-hand with the full Build → Decide → Act lifecycle. After creation you\'ll add skills + evals in the Build tab.';

  const footer = (
    <div className="flex items-center justify-end gap-2 w-full">
      <Button variant="secondary" onClick={onClose} disabled={submitting || savingDraft}>Cancel</Button>
      {typeof onSaveDraft === 'function' && (
        /* Save draft — persists the in-progress form server-side so it survives closing
         * the panel / leaving / switching devices. Available even with an incomplete name
         * (a draft is a work-in-progress); only blocked while a save/create is in flight. */
        <Button
          variant="ghost"
          onClick={onSaveDraft}
          disabled={submitting || savingDraft}
        >
          {savingDraft ? 'Saving…' : (draftId ? 'Update draft' : 'Save draft')}
        </Button>
      )}
      <Button
        variant="primary"
        onClick={onSubmit}
        disabled={submitting || savingDraft || !isNameValid}
        iconLeft={submitting ? null : <CheckCircle2 size={13} />}
      >
        {submitLabel}
      </Button>
    </div>
  );

  return (
    <Slideout
      isOpen={isOpen}
      onClose={submitting ? undefined : onClose}
      width={620}
      ariaLabel={title}
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Bot size={14} className="text-v2-action-strong" />
            <Pill tone={archetype === 'extelet' ? 'warning' : archetype === 'autolet' ? 'cyan' : archetype === 'syslet' ? 'info' : 'muted'} size="sm">
              {archetypeShortLabel(archetype)}
            </Pill>
          </div>
          <h2 className="text-[16px] font-bold m-0">{title}</h2>
          <p className="text-[12px] text-v2-ink-secondary mt-1 mb-0">{intro}</p>
        </div>
      }
      footer={footer}
    >
      <div className="space-y-4 text-[13px]">
        {/* ── Identity ────────────────────────────────────────────── */}
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">Name</label>
          <input
            type="text"
            autoFocus
            value={form?.name || ''}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={archetype === 'extelet' ? 'e.g. CrowdStrike Triage Agent' : 'e.g. IAM Sentry'}
            maxLength={NAME_MAX + 50 /* leave headroom over the limit so the
              hint can clearly tell the user they're over rather than
              silently truncating their input — DEF-VALIDATION-001 */}
            aria-invalid={nameLen > 0 && !isNameValid}
            className={`v2-focus-ring w-full px-3 py-2 rounded-v2-sm border bg-v2-surface text-v2-ink ${
              nameLen > 0 && !isNameValid ? 'border-v2-danger' : 'border-v2-border'
            }`}
          />
          {/* DEF-VALIDATION-001 — inline length hint, mirrors the submit
              button's disable state so the operator knows exactly why
              Create is greyed out. */}
          <div className={`mt-1 text-[11px] ${nameHintTone}`}>{nameHint}</div>
        </div>

        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">Description (optional)</label>
          <textarea
            value={form?.description || ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What is this Agentlet responsible for?"
            rows={2}
            className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink resize-y"
          />
        </div>

        {/* ════════════════════════════════════════════════════════════
            Phase 1 / V188 — five-dimension agent typing model.

              Function mode   — discipline    (compliance / threat / iam / appsec / devops / secops / general)
              Industry mode   — vertical      (financial / insurance / healthcare / retail / telco / ... / general)
              Operation mode  — authority     (platform / human / autonomous)
              Execution mode  — reasoning     (deterministic / cognitive / hybrid)
              trust_band      — earned        (platform-written via score waterfall; defaults to 'SUPERVISED')

            Each dropdown carries values that belong to ONLY that
            dimension — no more `cognitive` showing up as a "type" or
            `autonomous` showing up as a domain. V188 CHECK constraints
            reject any cross-mode combination that violates the
            invariant matrix; the inline advisory on
            operation_mode='autonomous' surfaces the most common
            rejection before submit.
        ════════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">
              Function mode <span className="text-v2-ink-secondary font-normal normal-case">— what discipline</span>
            </label>
            <select
              value={form?.functionMode || 'general'}
              onChange={(e) => {
                const v = e.target.value;
                /* Step 1 — opinionated defaults for the control_plane
                 * discipline. We patch operationMode + executionMode
                 * ONLY when the operator hasn't explicitly chosen
                 * non-default values for them (i.e. they're still at
                 * the form's blank/initial state). This preserves a
                 * conscious override every time. */
                if (v === 'control_plane') {
                  const next = { ...form, functionMode: v };
                  if (!form?.operationMode || form.operationMode === 'platform') {
                    next.operationMode = CONTROL_PLANE_DEFAULTS.operationMode;
                  }
                  if (!form?.executionMode || form.executionMode === 'deterministic') {
                    next.executionMode = CONTROL_PLANE_DEFAULTS.executionMode;
                  }
                  setForm(next);
                  return;
                }
                setForm({ ...form, functionMode: v });
              }}
              className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink"
            >
              {FUNCTION_MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {form?.functionMode === 'control_plane' && (
              <div className="mt-1.5 text-[11px] text-v2-action-strong bg-v2-action-soft/40 border border-v2-action/30 rounded-v2-sm px-2 py-1.5 leading-snug">
                <strong>Control-plane</strong> agents direct other agents.
                We've pre-selected <code>operation_mode=human</code> (every
                mutating action gated) and <code>execution_mode=deterministic</code>
                — both are safe defaults you can still change below.
              </div>
            )}
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">
              Industry mode <span className="text-v2-ink-secondary font-normal normal-case">— what vertical</span>
            </label>
            <select
              value={form?.industryMode || 'general'}
              onChange={(e) => setForm({ ...form, industryMode: e.target.value })}
              className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink"
            >
              {INDUSTRY_MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">
              Operation mode <span className="text-v2-ink-secondary font-normal normal-case">— who has authority</span>
            </label>
            <select
              value={form?.operationMode || 'platform'}
              onChange={(e) => setForm({ ...form, operationMode: e.target.value })}
              className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink"
            >
              {OPERATION_MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {form?.operationMode === 'autonomous' && (
              <div className="mt-1.5 text-[11px] text-v2-warning-strong bg-v2-warning-soft border border-v2-warning/30 rounded-v2-sm px-2 py-1.5 leading-snug">
                Autonomous requires trust_band=Full. Fresh agents start at Supervised, so this combination will be rejected. Pick <code>platform</code> or <code>human</code> now and promote later via the trust ledger.
              </div>
            )}
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">
              Execution mode <span className="text-v2-ink-secondary font-normal normal-case">— how it reasons</span>
            </label>
            <select
              value={form?.executionMode || 'deterministic'}
              onChange={(e) => setForm({ ...form, executionMode: e.target.value })}
              className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink"
            >
              {EXECUTION_MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <div className="mt-1.5 text-[11px] text-v2-ink-muted leading-snug">
              You can flip modes later from the canvas banner. Cognitive + Hybrid require a cognitive brief (system prompt + iteration cap + cost cap) configured before first run.
            </div>
          </div>
        </div>

        {/* ── Connectivity (Let → optional gateway; ExteLet → endpoint+key) ── */}
        {archetype === 'extelet' ? (
          <>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">Endpoint URL</label>
              <input
                type="url"
                value={form?.endpointUrl || ''}
                onChange={(e) => setForm({ ...form, endpointUrl: e.target.value })}
                placeholder="https://agent.example.com/api"
                className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink font-mono-v2 text-[12px]"
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">API key (optional)</label>
              <input
                type="password"
                value={form?.apiKey || ''}
                onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                placeholder="••••••••"
                className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink font-mono-v2 text-[12px]"
              />
              <div className="text-[11px] text-v2-ink-muted mt-1">Stored in the platform token vault. The agent receives this via the GEL connector at runtime.</div>
            </div>
          </>
        ) : (
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-v2-ink-muted mb-1.5">Gateway URL (optional)</label>
            <input
              type="url"
              value={form?.gatewayUrl || ''}
              onChange={(e) => setForm({ ...form, gatewayUrl: e.target.value })}
              placeholder="http://host.docker.internal:18790"
              className="v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink font-mono-v2 text-[12px]"
            />
          </div>
        )}

        {/* Accountability — who is accountable for this agent (SysLets are ownership-exempt). */}
        {archetype !== 'syslet' ? (
          <AccountabilitySection form={form} setForm={setForm} />
        ) : null}

        <div className="text-[11.5px] text-v2-ink-muted leading-relaxed bg-v2-action-soft border border-v2-action rounded-v2-sm px-3 py-2">
          <strong className="text-v2-action-strong">After creation:</strong> you'll land on the Agentlet detail page in the <strong>Build</strong> phase. Add at least one skill + eval there before promoting to Decide.
        </div>
      </div>
    </Slideout>
  );
}

export default AgentCreateSlideout;
